<template>
  <tbody class="js-simple-product-row">
    <tr>
      <!-- Product Image -->
      <td class="single-box__image" v-if="!hideImage">
        <Gallery
          :thumbnail="product.thumbnail"
          :displayOtherImages="false"
          :images="product.images"
          :canOpen="true"
        />
      </td>

      <!-- Product Title -->
      <td class="single-box__title" v-if="!hideName">
        <a class="product-name" :href="getProductLink()">
          <Badges 
            :is_on_sale="product.is_on_sale"
            :is_new="product.is_new"
            :is_out_of_stock="product.stock_status == 'outofstock'"
            :discount_percentage="product.discount_percentage || 0" />
          <h6>{{ productName | emptyDash }}</h6>
        </a>

        <div v-if="isDangerousGoods" class="product-dangerous-goods-info">
          {{ translations.dangerousGoodsInfo }}
        </div>
      </td>

      <td>{{ this.product.sku }}</td>

      <!-- Attribute Columns -->
      <td
        v-for="attributeTaxonomy in displayedAttributeTaxonomies"
        :key="`row_attribute_taxonomy_${attributeTaxonomy}`"
        :data-column="attributeTaxonomy"
      >
        <div class="shop-table__line">
          <div class="attribute__container">
            <div class="attribute" :key="`simple_${product.id}_single_${attributeTaxonomy}_attribute`">
              {{ getAttributeValue(product.attributes, attributeTaxonomy) | emptyDash }}
            </div>
          </div>
        </div>
      </td>

      <td v-if="!isRentCategory" class="single-box__status">
        <span :class="'status__' + product.stock_status">{{ getStockLabel }}</span>
      </td>

      <td class="single-box__quantity" data-html2canvas-ignore>
        <div v-if="packages.length && isPurchasable" class="quantity__packages">
          <select name="package_type" ref="package_select">
            <option
              v-for="productPackage in packages"
              :key="productPackage.package_type_id"
              :value="productPackage.package_type_id"
              :selected="productPackage.package_type_id === selectedPackage"
            >
              {{ productPackage.package_type_name }}
              <template v-if="productPackage.package_type_id != 'PT'">
                ({{ inputToString(productPackage.qnty_in_package) }}{{ measurementUnit }})
              </template>
            </option>
          </select>
        </div>
        <div v-if="isPurchasable || this.customForm === 'offer'" class="quantity__wrapper">
          <button v-on:click="changeInputQuantity('decrease', $event)" class="js-quantity-down">
            <i data-feather="minus"></i>
          </button>
            <div class="quantity__amount">
              <input
              :min="minQuantity"
              :max="maxQuantity"
              :step="getPackageStep()"
              name="quantity"
              type="text"
              :value="inputValue"
              ref="add_to_cart_input"
              @invalid="customValidation"
              @change="checkBuyingStep"
            />
            <span v-if="showMeasurementUnit">{{ measurementUnit }}</span>
          </div>
          <button  v-on:click="changeInputQuantity('increase', $event)" class="js-quantity-up">
            <i data-feather="plus"></i>
          </button>
        </div>
      </td>

      <td class="single-box__price">
        <div class="single-box__price__wrapper">
          <Price
          :salePrice="getCurrentPackagePrice(Number(product.sale_price))"
          :regularPrice="getCurrentPackagePrice(Number(product.regular_price))"
          />
          <span class="single-box__price__sale" v-if="product.sale_price"><i data-feather="percent"></i></span>
        </div>
      </td>

      <td class="single-box__add-to-cart" data-html2canvas-ignore>
        <form v-if="isPurchasable" :action="product.permalink" method="post" enctype="multipart/form-data" v-on:submit.prevent="addToCart"
        class="add-to-cart-form">
          <button
            type="submit"
            name="submit"
            :style="getAddToCartColor() ? { 'background-color': getAddToCartColor() } : {}"
            class="button"
          >
            <i data-feather="shopping-cart"></i>
            {{ translations.addToCart }}
          </button>

          <input type="hidden" name="action" value="add_to_cart" />
          <input type="hidden" name="product_id" :value="product.id" />
          <input type="hidden" name="quantity" :value="currentQuantity" />
          <input v-if="selectedPackage" type="hidden" name="package_type" :value="selectedPackage" />
        </form>

        <template v-else>
          <button
            @click="showForm('rent')"
            type="button"
            v-if="customForm === 'rent'"
            class="out-of-stock"
          >
            <i data-feather="message-square"></i>
            {{ translations.rentOutOfStock }}
          </button>

          <button
            @click="showForm('offer')"
            type="button"
            v-else-if="customForm === 'offer'"
            class="out-of-stock"
          >
            <i data-feather="message-square"></i>
            {{ translations.askForOffer }}
          </button>

          <button
            @click="showForm('')"
            type="button"
            v-else
            class="out-of-stock"
          >
            <i data-feather="message-square"></i>
            {{ translations.askForAvailability }}
          </button>
        </template>
      </td>

      <td class="single-box__wishlist" v-if="displayWishlist" data-html2canvas-ignore>
        <Wishlist
          :productId="product.id"
          :regularPrice="Number(product.regular_price)"
          :salePrice="Number(product.sale_price)"
          :quantity="Number(currentQuantity)"
          :selectedPackage="selectedPackage"
        />
      </td>

      <!-- Other Info Column -->
      <td class="single-box__other" v-if="showOtherInfo">
        <div class="single-box__excerpt">{{ product.excerpt }}</div>
      </td>
    </tr>
  </tbody>
</template>

<script>
import Badges from '@/layouts/general-ui/Badges.vue';
import Price from '@/layouts/general-ui/Price.vue';
import Gallery from '@/layouts/general-ui/Gallery.vue';
import Wishlist from "@/layouts/general-ui/Wishlist.vue";
import FragmentsRenderer from '@/externals/FragmentRenderer';
import { Mutex } from 'async-mutex';
import Decimal from 'decimal.js';
import Choices from 'choices.js';
import feather from 'feather-icons';

let lock = new Mutex();

export default {
  name: 'SimpleRow',
  components: {
    Badges,
    Price,
    Gallery,
    Wishlist
  },
  props: {
    product: Object,
    showOtherInfo: Boolean,
    displayedAttributeTaxonomies: Array,
    showDiscountLabel: {
      type: Boolean,
      default() {
        return false;
      },
    },
    hideImage: {
      type: Boolean,
      default() {
        return false;
      },
    },
    hideName: {
      type: Boolean,
      default() {
        return false;
      },
    },
    hideSKU: {
      type: Boolean,
      default() {
        return false;
      },
    },
    priceLabel: {
      type: String,
      default() {
        return '';
      },
    },
    displayWishlist: {
      type: Boolean,
      default() {
        return ws_vue_store.display_wishlist === "1";
      },
    },
  },
  mounted() {
    if (this.$refs.package_select) {
      new Choices(this.$refs.package_select, {
        searchEnabled: false,
        searchChoices: false,
        shouldSort: false
      });
      this.$refs.package_select.addEventListener('change', e => this.packageSelected(e));
    }

    feather.replace();
  },
  data() {
    this.isWishlistView = this.$router.currentRoute.name === "Wishlist";
    this.viewWishlistData = this.$store.getters.getViewWishlistItem(this.product.id);

    let selectedPackage;
    if (this.isWishlistView && this.viewWishlistData && this.viewWishlistData.package_type) {
      selectedPackage = this.viewWishlistData.package_type;
    } else {
      selectedPackage = this.getSelectedPackage();
    }

    let isPackageless = ( ! selectedPackage || selectedPackage === 'PT');
    let packages = this.parsePackageField();
    if (packages.length && ! selectedPackage) {
      selectedPackage = 'PT'; // Default to packageless option
    }
    let selectedPackageData = selectedPackage ? packages.find(e => e.package_type_id == selectedPackage) : undefined;
    let minQuantity = this.getPackageMinQty();

    let currentQuantity;
    if (this.isWishlistView && this.viewWishlistData && this.viewWishlistData.quantity > 0) {
      currentQuantity = Number(this.viewWishlistData.quantity);
    } else {
      currentQuantity = minQuantity;
    }

    return {
      isLoading: false,
      changeAddToCartColor: false,
      errorMessage: '',
      lastValidation: 0,
      inputValue: this.inputToString(currentQuantity),
      showMeasurementUnit: ws_vue_store.show_measurement_unit === '1',
      packages: packages,
      selectedPackage: selectedPackage,
      isPackageless: isPackageless,
      selectedPackageData: selectedPackageData,
      currentQuantity: currentQuantity,
      minQuantity: minQuantity,
      fixedLength: ws_vue_store.fixed_length,
      translations: {
        readMore: ws_vue_store.translations.read_more,
        productSKU: ws_vue_store.translations.product_SKU,
        imageInfo: ws_vue_store.translations.image_info,
        withVat: ws_vue_store.translations.included_vat,
        withoutVat: ws_vue_store.translations.excluded_vat,
        product_added_to_cart: ws_vue_store.translations.product_added_to_cart,
        askForOffer: ws_vue_store.translations.offer_product,
        askForAvailability: ws_vue_store.translations.ask_for_availability,
        rentOutOfStock: ws_vue_store.translations.rent_out_of_stock,
        addToCart: ws_vue_store.translations.add_to_cart,
        instock: ws_vue_store.translations.instock,
        outofstock: ws_vue_store.translations.out_of_stock,
        backorder: ws_vue_store.translations.backorder,
        offer:ws_vue_store.translations.offer_status,
        dangerousGoodsInfo: ws_vue_store.translations.dangerous_goods_info
      },
    };
  },
  filters: {
    emptyDash(value) {
      if (!value) return '-';
      return value;
    },
  },
  computed : {
    productName() {
      // Convert special characters from HTML entity codes to characters
      let input = document.createElement("textarea");
      input.innerHTML = this.product.name;
      return input.value;
    },
    includingTax() {
      return this.$store.getters.getTaxesEnabled;
    },
    isPurchasable() {
      return this.product.stock_status === 'instock'  && !this.customForm;
    },
    customForm() {
      return (this.product.custom_form) ? this.product.custom_form : '';
    },
    measurementUnit() {
      return this.product.measurement_unit != undefined && this.showMeasurementUnit ? this.product.measurement_unit : '';
    },
    maxQuantity() {
      return Number(this.product.get_max_purchase_quantity);
    },
    isRentCategory() {
      return this.$store.getters.getIsRentCategory;
    },
    isDangerousGoods() {
      return this.product.dangerous_goods === 'yes';
    },
    getStockLabel() {
      switch (this.product.stock_status) {
        case 'instock':
          return this.translations.instock;
        case 'outofstock':
          return this.translations.outofstock;
        case 'onbackorder':
          return this.translations.backorder;
        case 'offer':
          return this.translations.offer;
      }
      return this.product.stock_status;
    }
  },
  methods: {
    parsePackageField() {
      if (this.product.packages) {
        try {
          let data = JSON.parse(this.product.packages);
          if (data.find) {
            return data; // Check if parsed data is array
          }
        } catch {}
      }
      return [];
    },
    getProductLink() {
      return ws_vue_store.site_url + this.product.permalink;
    },
    getSelectedPackage() {
      let selectedPackages = this.$store.getters.getSelectedPackages;
      return selectedPackages[this.product.id] ? selectedPackages[this.product.id] : '';
    },
    getCurrentPackageData() {
      if (this.selectedPackage) {
        return this.packages.find(e => e.package_type_id == this.selectedPackage);
      }
      return undefined;
    },
    triggerQuantityChange(qty) {
      if (this.isWishlistView && this.$store.getters.getIsViewWishlistOwner && this.customForm !== 'offer') {
        this.$store.dispatch('updateWishlist', {
          old_quantity: this.currentQuantity,
          price: this.product.sale_price ? this.product.sale_price : this.product.regular_price,
          product_id: this.product.id,
          package: this.selectedPackage,
          quantity: qty
        });
      }

      this.currentQuantity = qty;
      this.$forceUpdate();
    },
    triggerPackageChanged() {
      this.selectedPackage = this.getSelectedPackage();
      this.isPackageless = ( ! this.selectedPackage || this.selectedPackage === 'PT');
      this.currentQuantity = this.isPackageless ? this.minQuantity : 1;
      this.selectedPackageData = this.getCurrentPackageData(),
      this.$forceUpdate();
    },
    getCurrentPackagePrice(price) {
      return Number((price * this.currentQuantity).toFixed(this.fixedLength));
    },
    checkIfAttributeExists: function(attributes, attr){
      if(attr == undefined) return false;
      const found = attributes.find(attribute => attribute.key == attr);
      return found != undefined ? true : false;
    },
    getAttributeLabel(taxonomyName) {
      const taxonomy = this.$store.getters.getAttributeTaxonomy(taxonomyName);
      if (taxonomy) {
        return taxonomy.label;
      }
      return '';
    },
    getAttributeValue: function(attributes, attributeTaxonomy){
      const foundAttr = attributes.find(attribute => attribute.key == attributeTaxonomy);
      if(foundAttr == undefined) return '';

      let value = foundAttr.options.map(option => option.name).join(', ');
      // Decode html entity codes
      let textArea = document.createElement('textarea');
      textArea.innerHTML = value;

      return textArea.value;
    },
    getPackageSizeColumn() {
      if (this.packages.length > 1) {
        let quatities = [];
        for (const packageData of this.packages) {
          quatities.push(packageData['qnty_in_package']);
        }
        return quatities.join('/');
      }
      return '';
    },
    getPackageMinQty() {
      if (this.isPackageless || this.selectedPackageData === undefined) {
        return Number(this.product.get_min_purchase_quantity);
      } else {
        return this.selectedPackageData.qnty_in_package;
      }
    },
    getPackageStep() {
      if (this.isPackageless || this.selectedPackageData === undefined) {
        return this.product.buying_step != undefined ? this.product.buying_step : 1;
      } else {
        return this.selectedPackageData.qnty_in_package;
      }
    },
    // AddToCart.vue functions
    async addToCart(event) {
      if (this.checkBuyingStep()) {
        return; // Stop processing when not enough window is open
      };

      this.isLoading = true;
      let release = await lock.acquire();

      let formData = new FormData(event.target);
      if (formData.get('quantity')) {
        formData.set('quantity', this.inputToNumber(formData.get('quantity')));
      }

      this.axios
        .post(
          wc_add_to_cart_params.wc_ajax_url
            .toString()
            .replace('%%endpoint%%', 'add_to_cart'),
          formData
        )
        .then((result) => {
          if (result.data.fragments) {
            this.isLoading = false;
            FragmentsRenderer.renderFragments(result.data.fragments);
            this.displayWoocommerceNotice(this.translations.product_added_to_cart, false);
            this.$store.commit('setRecentlyAddedProduct', this.product.id);
          } else if (result.data.error) {
            this.getErrorMessages();
          }
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          release();
        });
    },
    getErrorMessages() {
      const self = this;
      this.axios
        .get(ws_vue_store.ajax_url + '?action=get_cart_error_messages')
        .then((response) => {
          self.isLoading = false;
          if (response.data) {
            this.displayWoocommerceNotice(response.data, 'woocommerce-error');
          }
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error(err);
        });
    },
    displayWoocommerceNotice(message, extraClass) {
      document.querySelectorAll('.woocommerce-notices-wrapper').forEach(wrapper => {
        wrapper.remove();
      });

      let wrapper = document.createElement('div');
      let notice = document.createElement('div');

      wrapper.classList.add('woocommerce-notices-wrapper');
      notice.classList.add('woocommerce-message');
      if (extraClass) notice.classList.add(extraClass);
      notice.role = 'alert';
      notice.tabIndex = -1;
      notice.innerHTML = message;

      wrapper.append(notice);
      document.querySelector('#content').append(wrapper);
    },
    showForm(type) {
      let formID = '';
      switch (type) {
        case 'rent':
          formID = '#rent-out-of-stock-form'; break;
        case 'offer':
          formID = '#offer-product-form'; break;
        default:
          formID = '#out-of-stock-form';
      }

      const modalForm = document.querySelector(formID);
      const pageShadow = document.getElementById('js-page-shadow');
      const pageBody = document.querySelector('body');

      if (!modalForm)
        return;

      let subjectContent;
      if (type == 'offer') {
        subjectContent = this.productName + ' - ' + this.product.sku + ' - ' + this.$refs.add_to_cart_input.value;
      } else {
        subjectContent = this.productName + ' - ' + this.product.sku;
      }

      modalForm
        .querySelector('input[name="your-subject"]')
        .value = subjectContent;
      modalForm
        .querySelector('.js-out-of-stock-subject')
        .innerText = subjectContent;

      modalForm.classList.add('active');
      pageShadow.style.display = 'block';
      pageBody.style.overflowY = 'hidden';
    },
    getAddToCartColor() {
      if (
        ws_vue_store.change_add_to_cart_color == '1' &&
        (this.changeAddToCartColor ||
          ws_vue_store.cart_items.includes(this.product.id) ||
          this.$store.getters.getRecentlyAddedProducts.includes(this.product.id))
      ) {
        return ws_vue_store.add_to_cart_color;
      }
      return '';
    },
    // AddToCart.vue -> Wells.vue functions

    // Convert a valid number to string with a different comma separator
    inputToString(value) {
      return value.toString().replace('.', this.$store.state.numberSeparator);
    },
    // Convert a string number with a custom separator to a valid number
    inputToNumber(value) {
      return Number(value.replace(this.$store.state.numberSeparator, '.'));
    },
    customValidation() {
      this.checkBuyingStep();
      let el = this.$refs.add_to_cart_input;
      let max = el.getAttribute('max');
      let value = this.inputToNumber(this.inputValue);

      if (parseInt(value) > parseInt(max)) {
        el.setCustomValidity(ws_vue_store.translations.add_to_cart_invalid_message);
      } else {
        el.setCustomValidity('');
      }
    },
    checkBuyingStep() {
      this.lastValidation = Date.now();
      let stopAddToCart = false;
      let quantity = this.inputToNumber(this.$refs.add_to_cart_input.value);
      if (isNaN(quantity)) quantity = this.minQuantity;

      if (this.isOpenNotEnoughForm()) {
        stopAddToCart = true;
      } else if (quantity > this.maxQuantity) {
        this.showNotEnoughForm();
        stopAddToCart = true;
      }

      this.setInputValue(quantity, this.minQuantity, this.maxQuantity);
      this.triggerQuantityChange(quantity);
      return stopAddToCart;
    },
    isOpenNotEnoughForm() {
      const modalForm = document.getElementById('ws-not-enough-form');
      return modalForm && modalForm.classList.contains('active');
    },
    packageSelected(event) {
      this.$store.commit('addSelectedPackages', {
        id: this.product.id,
        package: event.currentTarget.value
      });
      this.triggerPackageChanged();

      let selectedPackageData = this.packages.find(e => e.package_type_id == event.currentTarget.value);
      if (selectedPackageData) {
        this.inputValue = this.inputToString(selectedPackageData.qnty_in_package);
        this.triggerQuantityChange(selectedPackageData.qnty_in_package.toString());
      }
    },

    showNotEnoughForm() {
      const modalForm = document.getElementById('ws-not-enough-form');
      const pageShadow = document.getElementById('js-page-shadow');
      const pageBody = document.querySelector('body');

      if (!modalForm || modalForm.classList.contains('active')) return;

      let maxQty = this.maxQuantity;
      let subjectContent = this.productName + ' - ' + this.product.sku;
      let unit = this.measurementUnit;

      modalForm.querySelector('.product-name').textContent = subjectContent;
      modalForm.querySelector('input[name="your-subject"]').value = subjectContent;
      modalForm.querySelector('.js_not_enough_qty').innerText = maxQty;
      modalForm.querySelector('.js_not_enough_unit').innerText = unit;

      modalForm.classList.add('active');
      pageShadow.style.display = 'block';
      pageBody.style.overflowY = 'hidden';
    },

    changeInputQuantity(trend, event) {
      event.preventDefault();

      // Pressing "enter" key on quantity input also triggers "-" button
      // This checks that "enter" key wasn't used right now for changing quantity
      if ((Date.now() - this.lastValidation) < 500) {
        return;
      }

      let el = this.$refs.add_to_cart_input;
      let max = new Decimal(el.getAttribute('max'));
      let min = new Decimal(el.getAttribute('min'));
      let step = new Decimal(el.getAttribute('step'));

      let current_quantity = this.inputToNumber(el.value);
      current_quantity = new Decimal(current_quantity);

      let new_value = new Decimal(0);
      if (trend === 'increase' && (current_quantity.plus(step)).lte(max)) {
        new_value = current_quantity.plus(step);
      }
      if (trend === 'decrease' && (current_quantity.minus(step)).gte(min)) {
        new_value = current_quantity.minus(step);
      }

      if (!new_value.equals(new Decimal(0))) {
        this.inputValue = this.inputToString(new_value);
        this.triggerQuantityChange(new_value.toString());
      }
    },

    setInputValue(quantity, step, maxQuantity) {
      let calculation;

      if (+(quantity % step).toFixed(2) !== 0 || quantity > maxQuantity) {
        if (Math.ceil(quantity / step) * step > maxQuantity) {
          let qty = Math.ceil(maxQuantity / step);
          calculation = qty * step <= maxQuantity ? qty * step : qty * step - step;
        } else {
          calculation = Math.ceil(quantity / step) * step;
        }

        // Round the value
        if (Number.isSafeInteger(step)) {
          calculation = Math.round(calculation);
        } else {
          calculation = +(calculation).toFixed(2);
        }
      } else {
        calculation = quantity;
      }

      this.inputValue = this.inputToString(Math.abs(calculation));
      this.$refs.add_to_cart_input.value = this.inputValue;
    }
  }
};
</script>

<style lang="scss" scoped>
.single-box {
  display: grid;
  grid-template-columns: 6rem 20rem 0.2fr 0.1fr 0.1fr 0.1fr 0.1fr 0.4fr 12rem;
  padding: 2rem 0;
  border-bottom: 0.1rem solid lightgray;
  gap: 0 2rem;

  &__name {
    display: block;
    grid-row: 1 / last-line;
    grid-row-start: span 9000;
  }
}
</style>
